import {
  tableMemo,
  type Column,
  type Row,
  type RowData,
  type RowModel,
  type Table,
} from '@tanstack/react-table';

import type { DataTableColumnType } from '../ColumnTypes/column-types-types.js';
import type { TDataTableFeatures } from '../types/features.types.js';
import type { TableMemoTable } from '../types/table.types.js';
import { column_getCanSort, column_getCustomSortingFn } from './Sorting.js';

export function createSortedRowModel<TData extends RowData>(): (
  table: Table<TDataTableFeatures, TData>,
) => () => RowModel<TDataTableFeatures, TData> {
  return (table) => {
    return tableMemo({
      feature: 'rowSortingFeature',
      table: table as unknown as TableMemoTable,
      fnName: 'table.getSortedRowModel',
      memoDeps: () => [
        table.atoms.sorting?.get(),
        table.getPreSortedRowModel(),
      ],
      fn: () => _createSortedRowModel(table),
    });
  };
}

/**
 *
 * Sorted row model copied from the tanstack/react-table and adapted
 * for handling null and undefined values and control over custom sorting functionality.
 */
function _createSortedRowModel<
  // oxlint-disable-next-line typescript/no-explicit-any
  TData extends RowData = any,
>(
  table: Table<TDataTableFeatures, TData>,
): RowModel<TDataTableFeatures, TData> {
  const preSortedRowModel = table.getPreSortedRowModel();
  const sortingState = table.atoms.sorting?.get();

  if (!preSortedRowModel.rows.length || !sortingState?.length) {
    return preSortedRowModel;
  }

  const sortedFlatRows: Row<TDataTableFeatures, TData>[] = [];

  // Filter out sortings that correspond to non existing columns
  const availableSorting = sortingState.filter((sort) =>
    column_getCanSort(
      table.getColumn(sort.id) as Column<TDataTableFeatures, TData>,
    ),
  );

  const columnInfoById: Record<
    string,
    {
      sortUndefined?: false | -1 | 1 | 'first' | 'last';
      invertSorting?: boolean;
      columnType?: DataTableColumnType;
      customSortingFn: (valueA: unknown, valueB: unknown) => number;
    }
  > = {};

  availableSorting.forEach((sortEntry) => {
    // Requires a cast in order to access the sorting definitions that are only available when the features is enabled. The TDataTableFeatures inheritance is lost through the call.

    const column = table.getColumn(sortEntry.id) as Column<
      TDataTableFeatures,
      TData
    >;
    if (!column) {
      return;
    }

    columnInfoById[sortEntry.id] = {
      sortUndefined: column.columnDef.sortUndefined,
      invertSorting: column.columnDef.invertSorting,
      columnType: column.columnDef.columnType,
      customSortingFn: column_getCustomSortingFn(column),
    };
  });

  const sortData = (rows: Row<TDataTableFeatures, TData>[]) => {
    // This will also perform a stable sorting using the row index
    // if needed.
    // Preserve prototype chain so methods like getValue() remain accessible
    const sortedData = rows.map((row) => {
      const cloned = Object.create(Object.getPrototypeOf(row));
      return Object.assign(cloned, row);
    });

    sortedData.sort((rowA, rowB) => {
      for (let i = 0; i < availableSorting.length; i += 1) {
        const sortEntry = availableSorting[i];
        const columnInfo = columnInfoById[sortEntry.id];
        const isDesc = sortEntry?.desc ?? false;

        let sortInt = 0;

        // All sorting ints should always return in ascending order
        if (columnInfo.sortUndefined) {
          const aValue = rowA.getSortingValue(sortEntry.id);
          const bValue = rowB.getSortingValue(sortEntry.id);

          const aUndefined =
            aValue === undefined ||
            aValue === null ||
            (columnInfo.columnType !== undefined && Number.isNaN(aValue));
          const bUndefined =
            bValue === undefined ||
            bValue === null ||
            (columnInfo.columnType !== undefined && Number.isNaN(bValue));

          if (aUndefined || bUndefined) {
            if (aUndefined && bUndefined) {
              sortInt = 0;
            }
            // sort null and undefined always at the bottom
            // sort NaN at the bottom if `columnType` is defined otherwise NaN is compared alphanumerically by default
            const sorting =
              (columnInfo.invertSorting && isDesc) ||
              (!columnInfo.invertSorting && !isDesc);

            if (aUndefined) {
              sortInt = sorting ? 1 : -1;
            } else {
              sortInt = sorting ? -1 : 1;
            }
          }
        }

        if (sortInt === 0) {
          sortInt = columnInfo.customSortingFn(
            rowA.getSortingValue(sortEntry.id),
            rowB.getSortingValue(sortEntry.id),
          );
        }

        // If sorting is non-zero, take care of desc and inversion
        if (sortInt !== 0) {
          if (isDesc) {
            sortInt *= -1;
          }

          if (columnInfo.invertSorting) {
            sortInt *= -1;
          }

          return sortInt;
        }
      }

      return rowA.index - rowB.index;
    });

    // If there are sub-rows, sort them
    sortedData.forEach((row) => {
      sortedFlatRows.push(row);
      if (row.subRows?.length) {
        row.subRows = sortData(row.subRows);
      }
    });

    return sortedData;
  };

  return {
    rows: sortData(preSortedRowModel.rows),
    flatRows: sortedFlatRows,
    rowsById: preSortedRowModel.rowsById,
  };
}
